import React, { useMemo, useState } from 'react';
import { MOCK_CALLS } from '../../live-monitor/mockData';
import type { LiveMonitorDemoState, LiveMonitorFilterId } from '../../live-monitor/types';
import { LiveMonitorCallDrawer } from './LiveMonitorCallDrawer';
import { LiveMonitorCallTile } from './LiveMonitorCallTile';
import { LiveMonitorEmpty } from './LiveMonitorEmpty';
import { LiveMonitorFilter } from './LiveMonitorFilter';
import { LiveMonitorHero } from './LiveMonitorHero';
import { LiveMonitorKpiStrip } from './LiveMonitorKpiStrip';
import './live-monitor.css';

export interface LiveMonitorViewProps {
  demoState?: LiveMonitorDemoState;
}

export function LiveMonitorView({ demoState = 'active' }: LiveMonitorViewProps) {
  const [filter, setFilter] = useState<LiveMonitorFilterId>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const calls = demoState === 'active' ? MOCK_CALLS : [];

  const counts = useMemo(
    () => ({
      all: calls.length,
      needs: calls.filter((c) => c.urgent || c.onHold).length,
      inbound: calls.filter((c) => c.direction === 'inbound').length,
      outboundPatient: calls.filter((c) => c.direction === 'outbound-patient').length,
      outboundInsurance: calls.filter((c) => c.direction === 'outbound-insurance').length,
    }),
    [calls],
  );

  const filtered = useMemo(() => {
    if (filter === 'all') return calls;
    if (filter === 'needs') return calls.filter((c) => c.urgent || c.onHold);
    return calls.filter((c) => c.direction === filter);
  }, [calls, filter]);

  const selected = useMemo(
    () => calls.find((c) => c.id === selectedId) ?? null,
    [calls, selectedId],
  );

  return (
    <div className="lm-root mx-auto min-h-0 w-full max-w-[1400px] flex-1 overflow-y-auto px-9 pb-[60px] pt-8">
      <LiveMonitorHero calls={calls} />
      <LiveMonitorKpiStrip />

      <section className="lm-fade-up lm-d-2 mb-[18px]">
        <header className="mb-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span
              className="lm-mono rounded-[5px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2 py-0.5 text-[11px] font-semibold text-[var(--ink-2)] shadow-[var(--sh-1)]"
            >
              01
            </span>
            <span
              className="rounded-sm"
              style={{ width: 3, height: 22, background: '#0F5C45' }}
            />
            <div>
              <div className="text-[19px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                In progress
              </div>
              <div className="mt-0.5 text-[12px] text-[var(--ink-2)]">
                Sorted by urgency. Frustrated and long calls float to the top.
              </div>
            </div>
          </div>
          <span className="lm-mono text-[11px] uppercase tracking-[0.06em] text-[var(--ink-2)]">
            {filtered.length} shown
          </span>
        </header>

        <div className="mb-[18px]">
          <LiveMonitorFilter value={filter} onChange={setFilter} counts={counts} />
        </div>

        {filtered.length === 0 ? (
          <LiveMonitorEmpty />
        ) : (
          <div
            className="grid gap-3.5"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}
          >
            {filtered.map((call) => (
              <LiveMonitorCallTile
                key={call.id}
                call={call}
                isSelected={selectedId === call.id}
                onClick={() => setSelectedId(call.id)}
              />
            ))}
          </div>
        )}
      </section>

      <footer className="mt-7 flex items-center gap-2.5 border-t border-[var(--cream-2)] py-4 text-[12px] text-[var(--ink-2)]">
        <span className="elva-live h-[6px] w-[6px]" />
        <span className="font-medium">Live</span>
        <span className="lm-mono">· Tiles update every 250ms</span>
        <span className="lm-mono ml-auto text-[11px] text-[var(--ink-2)] opacity-70">
          elva v2 · apple era · live monitor
        </span>
      </footer>

      {selected && <LiveMonitorCallDrawer call={selected} onClose={() => setSelectedId(null)} />}
    </div>
  );
}
